import { SortingState } from '@tanstack/react-table'
import { FilterWorkersWithVisibleItems } from 'components/buttons/FilterWorkersWithVisibleItemsButtonGroup'
import { useLocalStorageState } from 'hooks/use-local-storage-state'
import { createSetOperationsFor } from '@utils/createSetOperations'
import { createStringOperationsFor } from '@utils/createStringOperations'
import { createSortingOperationsFor } from '@utils/createSortingOperations'
import { useMemo } from 'react'
import { Status } from 'types'
import { Anomalies } from 'components/buttons/AnomalousTimeCardButtons'

interface ApprovalsLocalStorageState {
  hiddenDays: Set<number>
  hiddenStatuses: Set<Status>
  filteredAnomalyTypes: Set<Anomalies>
  filteredFacilityIds: Set<number>
  filteredApprovalDomainIds: Set<string>
  filteredDepartmentIds: Set<string>
  filterEmployeesWithVisibleApprovals: FilterWorkersWithVisibleItems
  sorting: SortingState
  employeeNameFilter: string
}

const defaultValues: ApprovalsLocalStorageState = {
  hiddenDays: new Set(),
  hiddenStatuses: new Set(),
  filteredAnomalyTypes: new Set(),
  filteredFacilityIds: new Set(),
  filteredApprovalDomainIds: new Set(),
  filteredDepartmentIds: new Set(),
  filterEmployeesWithVisibleApprovals: 'all',
  sorting: [
    {
      id: 'employeeName',
      desc: false,
    },
  ],
  employeeNameFilter: '',
}

export const useApprovalsLocalStorage = () => {
  const { state, setState, debounced, clear, changedAttributes, isChanged } =
    useLocalStorageState('timeLoggerApprovalsState', defaultValues)

  return useMemo(
    () => ({
      hiddenDays: createSetOperationsFor('hiddenDays')(state, setState),
      hiddenStatuses: createSetOperationsFor('hiddenStatuses')(state, setState),
      filteredAnomalyTypes: createSetOperationsFor('filteredAnomalyTypes')(
        state,
        setState,
      ),
      filteredFacilityIds: {
        ...createSetOperationsFor('filteredFacilityIds')(state, setState),
        debounced: debounced?.filteredFacilityIds ?? new Set(),
      },
      filteredApprovalDomainIds: {
        ...createSetOperationsFor('filteredApprovalDomainIds')(state, setState),
        debounced: debounced?.filteredApprovalDomainIds ?? new Set(),
      },
      filteredDepartmentIds: {
        ...createSetOperationsFor('filteredDepartmentIds')(state, setState),
        debounced: debounced?.filteredDepartmentIds ?? new Set(),
      },
      filterEmployeesWithVisibleApprovals: {
        value: state.filterEmployeesWithVisibleApprovals,
        debounced: debounced?.filterEmployeesWithVisibleApprovals ?? '',
        set: (value: FilterWorkersWithVisibleItems) => {
          setState((prev) => ({
            ...prev,
            filterEmployeesWithVisibleApprovals: value,
          }))
        },
      },
      sorting: createSortingOperationsFor('sorting')(state, setState),
      employeeNameFilter: {
        ...createStringOperationsFor('employeeNameFilter')(state, setState),
        debounced: debounced?.employeeNameFilter ?? '',
      },
      clear,
      changedAttributes,
      isChanged,
    }),
    [state, setState, debounced, clear, changedAttributes, isChanged],
  )
}
